import RouteTemplate from 'ember-route-template'
import { ReportingTeacherUiHeader } from 'teacher-ui-client/components/reporting/teacher-ui-header'
import { ReportingStudentSummaryControls } from 'teacher-ui-client/components/reporting/student-summary-controls'
import { ReportingNoDataMessageIf } from 'teacher-ui-client/components/reporting/no-data-message-if'
import { array } from '@ember/helper'
import { Precinct } from 'teacher-ui-client/utils/precincts'
import { ExportType } from 'teacher-ui-client/components/reporting/export-select'
import { StudentChartWrapper } from 'teacher-ui-client/components/student-chart-wrapper'
import { CourseProgressStudent } from 'teacher-ui-client/components/giraffe/charts/course-progress/student'
import { AssessmentScoresStudent } from 'teacher-ui-client/components/giraffe/charts/assessment-scores/student'
import { LexileDetailsStudent } from 'teacher-ui-client/components/giraffe/charts/lexile-details/student'
import { DrivingTestsStudent } from 'teacher-ui-client/components/giraffe/charts/driving-tests/student'
import { BooksReadStudent } from 'teacher-ui-client/components/giraffe/charts/books-read/student'
import { StoriesWrittenStudent } from 'teacher-ui-client/components/giraffe/charts/stories-written/student'
import { StorylandsLessonsStudent } from 'teacher-ui-client/components/giraffe/charts/storylands-lessons/student'
import { AwardsEarnedCertificatesTotals } from 'teacher-ui-client/components/giraffe/charts/awards-earned/certificates/totals'
import type ReStudentSummaryIndexRoute from 'teacher-ui-client/reporting/ui-scope/re/student-summary/index/route'
import { not } from 'ember-truth-helpers'
import { t } from 'ember-intl'
import Component from '@glimmer/component'
import { isPresent } from '@ember/utils'
import { service } from '@ember/service'
import type { IntlService } from 'ember-intl'

interface Signature {
  Args: {
    model: ModelFor<ReStudentSummaryIndexRoute>
  }
}
export class ReStudentSummaryIndexPage extends Component<Signature> {
  @service intl!: IntlService

  readonly routePrefix = 'reporting.ui-scope.re.student-summary'

  get linkText() {
    return this.intl.t('reporting.seeMoreLink')
  }

  get drivingTestsLink() {
    const text = this.linkText
    return { text, route: `reporting.ui-scope.re.student-summary.driving-tests` }
  }

  get courseProgressReadingLink() {
    const text = this.linkText
    return { text, route: `reporting.ui-scope.re.student-summary.course-progress.reading` }
  }

  get storiesWrittenLink() {
    const text = this.linkText
    return { text, route: `reporting.ui-scope.re.student-summary.stories-written` }
  }

  get spellingCompletedLink() {
    const text = this.linkText
    return { text, route: `reporting.ui-scope.re.student-summary.course-progress.spelling` }
  }

  get pageTitle() {
    const name = this.args.model?.student.fullName ?? ''
    return this.intl.t('reporting.re.studentSummary.pageTitle', { name })
  }

  // Checks all the charts for data, if no data this disables the export button
  get anyDataCheck() {
    return (
      isPresent(this.args.model.students) &&
      (isPresent(this.args.model.courseProgressReading) ||
        isPresent(this.args.model.assessmentScoresStudent) ||
        isPresent(this.args.model.drivingTestsStudent) ||
        isPresent(this.args.model.lexileDetailsStudent) ||
        isPresent(this.args.model.booksReadStudent) ||
        isPresent(this.args.model.courseProgressSpelling) ||
        isPresent(this.args.model.storiesWrittenStudent) ||
        isPresent(this.args.model.storylandsStudent) ||
        isPresent(this.args.model.awardsEarnedByMonthEvents))
    )
  }

  <template>
    <ReportingTeacherUiHeader @title={{this.pageTitle}} />

    <ReportingStudentSummaryControls
      @students={{@model.students}}
      @student={{@model.student}}
      @routePrefix={{this.routePrefix}}
      @exportOptions={{array ExportType.print}}
      @isDataPresent={{this.anyDataCheck}}
      @studentRouteName="reporting.ui-scope.re.student-summary"
      @allStudentsRouteName="reporting.ui-scope.re.dashboard"
    />

    <ReportingNoDataMessageIf @on={{not @model.students}}>
      <StudentChartWrapper @empty={{not @model.courseProgressReading}}>
        {{#if @model.courseProgressReading}}
          <CourseProgressStudent
            @data={{@model.courseProgressReading}}
            @precinct={{Precinct.RE_READING}}
            @period={{@model.period}}
            @link={{this.courseProgressReadingLink}}
            @summaryTitle="Lessons Completed"
          />
        {{/if}}
      </StudentChartWrapper>

      <StudentChartWrapper @empty={{not @model.assessmentScoresStudent}}>
        {{#if @model.assessmentScoresStudent}}
          <AssessmentScoresStudent
            @data={{@model.assessmentScoresStudent}}
            @summaryTitle={{t "reporting.summaryTitles.assessmentScoresStudent.title.re"}}
          />
        {{/if}}
      </StudentChartWrapper>

      <StudentChartWrapper @empty={{not @model.lexileDetailsStudent}}>
        {{#if @model.lexileDetailsStudent}}
          <LexileDetailsStudent @data={{@model.lexileDetailsStudent}} @product={{@model.product}} />
        {{/if}}
      </StudentChartWrapper>

      <StudentChartWrapper @empty={{not @model.drivingTestsStudent}}>
        {{#if @model.drivingTestsStudent}}
          <DrivingTestsStudent
            @data={{@model.drivingTestsStudent}}
            @product={{@model.product}}
            @link={{this.drivingTestsLink}}
          />
        {{/if}}
      </StudentChartWrapper>

      <StudentChartWrapper @empty={{not @model.booksReadStudent}}>
        {{#if @model.booksReadStudent}}
          <BooksReadStudent
            @data={{@model.booksReadStudent}}
            @product={{@model.product}}
            @period={{@model.period}}
            @summaryTitle={{t "reporting.summaryTitles.booksReadStudent"}}
          />
        {{/if}}
      </StudentChartWrapper>

      <StudentChartWrapper @empty={{not @model.courseProgressSpelling}}>
        {{#if @model.courseProgressSpelling}}
          <CourseProgressStudent
            @data={{@model.courseProgressSpelling}}
            @precinct={{Precinct.RE_SPELLING}}
            @summaryTitle={{t "reporting.summaryTitles.courseProgressStudentSpelling"}}
            @period={{@model.period}}
            @link={{this.spellingCompletedLink}}
          />
        {{/if}}
      </StudentChartWrapper>

      <StudentChartWrapper @empty={{not @model.storiesWrittenStudent}}>
        {{#if @model.storiesWrittenStudent}}
          <StoriesWrittenStudent
            @data={{@model.storiesWrittenStudent}}
            @summaryTitle={{t "reporting.summaryTitles.storiesWrittenStudent"}}
            @link={{this.storiesWrittenLink}}
          />
        {{/if}}
      </StudentChartWrapper>

      <StudentChartWrapper @empty={{not @model.storylandsStudent}}>
        {{#if @model.storylandsStudent}}
          <StorylandsLessonsStudent
            @data={{@model.storylandsStudent}}
            @summaryTitle={{t "reporting.summaryTitles.storylandsStudent"}}
          />
        {{/if}}
      </StudentChartWrapper>

      <StudentChartWrapper @empty={{not @model.awardsEarnedByMonthEvents}}>
        {{#if @model.awardsEarnedByMonthEvents}}
          <AwardsEarnedCertificatesTotals
            data-test-awards-earned-certificates-by-month
            class="relative"
            @data={{@model.awardsEarnedByMonthEvents}}
            @product={{@model.product}}
            @title={{t "reporting.chartTitles.awardsEarnedByMonthEvents"}}
            @summaryTitle={{t "reporting.summaryTitles.awardsEarnedByMonthEvents"}}
            @yAxisTitle={{t "reporting.yAxisLabels.awardsEarnedByMonthEvents"}}
          />
        {{/if}}
      </StudentChartWrapper>
    </ReportingNoDataMessageIf>
  </template>
}

export default RouteTemplate(ReStudentSummaryIndexPage)
